import { Icon } from '@aibees/shared/components';
import { useResponsive } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { cloneElement, memo, MouseEvent } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { TransferListOpeningProps as Props } from './types';

const ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'center' as const,
};
const TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'center' as const,
};

export const TransferListOpening = memo(
  ({
    className,
    dialogMode,
    children,
    state,
    element,
    icon,
    title,
    actionElement,
    onClose,
  }: Props) => {
    const mdDown = useResponsive('down', 'md');

    const handleClose = () => {
      state.onClose();
      onClose?.();
    };

    return dialogMode ? (
      <>
        {element &&
          cloneElement(element, {
            ...element.props,
            className: classnames(element.props.className, {
              open: state.open,
            }),
            onClick: (e: MouseEvent<HTMLElement>) => {
              state.onOpen(e.currentTarget as HTMLElement);
            },
          })}
        <Styled.Dialog
          className={'TransferListOpening'}
          open={!!state.open}
          fullWidth
          maxWidth={mdDown ? 'sm' : 'smplus'}
          onClose={handleClose}
        >
          {title && (
            <DialogTitle padding="normal">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Stack direction="row" gap={0.5} alignItems="center">
                  {icon && (
                    <Icon
                      name={icon || 'view-compact'}
                      size={22}
                      color="primary.500"
                    />
                  )}

                  <Typography variant="body2" fontWeight={400} color="grey.800">
                    {title}
                  </Typography>
                </Stack>

                <Icon
                  name="close"
                  size={16}
                  color="grey.700"
                  onClick={handleClose}
                />
              </Stack>
            </DialogTitle>
          )}

          <DialogContent padding="none">{children}</DialogContent>

          <DialogActions>{actionElement}</DialogActions>
        </Styled.Dialog>
      </>
    ) : (
      <Styled.Popover
        className={classnames('TransferListOpening', className)}
        sx={SX.root}
        state={state}
        icon={
          <Icon name={icon || 'view-compact'} size={24} color="primary.500" />
        }
        title={
          <Typography variant="body2" fontWeight={600} color="grey.700">
            {title}
          </Typography>
        }
        titleSx={SX.header}
        element={element}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
        contentSx={SX.mainContent(mdDown)}
        onClose={handleClose}
      >
        {children}
      </Styled.Popover>
    );
  }
);

TransferListOpening.displayName = 'TransferListOpening';
